/* Movatic WMS — Coletor | TaskOfferProvider (popup + poll GLOBAIS, ADR-0038 F7 pecas 2/3) | CesarFilho 932048016
   Arquivo: src/mobile/components/TaskOfferProvider.tsx
   ----------------------------------------------------------------------------
   A convocacao SOBREPOE-SE A TUDO (lei do desenho): o popup e o poll do inbox sobem ao layout
   raiz do coletor (dentro do gate de presenca) -> aparecem em QUALQUER pagina, nao so' na aba
   Tarefas. A peca 1 (backend, login convoca -> _maybe_convoke_on_login) ja' cria a atribuicao
   vigente; este poll pesca-a e abre o popup SEM o operador ir a' aba Tarefas. Aceitar -> segue
   para a execucao (/m/tasks). Rejeitar -> volta ao buffer + pausa (o backend trata; o poll retoma
   quando o operador voltar a ficar disponivel). O estado da OFERTA vive aqui (nao mais no Tasks.tsx),
   que passa a consumir apenas a task ACEITE (activeTask) para o loop de execucao. */
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  acceptTask,
  claimNextTask,
  getNextStep,
  getTaskInbox,
  heartbeatSession,
  rejectTask,
  type TaskDTO,
} from '@/api/workforce'
import TaskOfferPopup from './TaskOfferPopup'
import { useFeedback } from '../hooks/useFeedback'

type Ctx = {
  /** Task ACEITE, em execucao. A aba Tarefas le-a para o loop de recolha. */
  activeTask: TaskDTO | null
  /** Fim da execucao -> volta a ocioso (o poll do inbox retoma). */
  clearActive: () => void
  /** Pull manual (botao "Proxima tarefa" na aba Tarefas). Mostra a oferta (popup). */
  claimNext: () => void
  claiming: boolean
  claimError: string | null
}

// Exportado: o CatalogViewer (modo-operador) le-o via useContext p/ chamar clearActive ao
// concluir. useContext (nao useTaskOffer) porque o CatalogViewer TAMBEM monta na rota desktop
// /catalogo, FORA do provider -> ali o contexto e' null (nao rebenta), so' no /m e' que existe.
export const TaskOfferContext = createContext<Ctx | null>(null)

export function useTaskOffer(): Ctx {
  const c = useContext(TaskOfferContext)
  if (!c)
    throw new Error('useTaskOffer tem de estar dentro de <TaskOfferProvider>')
  return c
}

// ADR-0058 (coletor, item 3): só se mostra como OFERTA uma task ainda ofertável (não aceite nem
// terminal). done/cancelled/accepted/in_progress -> não é oferta viva.
const OFFERABLE_STATUS = new Set(['assigned', 'pending'])
function isOfferable(t: TaskDTO | null | undefined): boolean {
  return !!t && OFFERABLE_STATUS.has(String((t as { status?: string }).status))
}

// ADR-0058 (item 1): mensagem honesta a partir do erro (axios). Distingue por status; nunca engole
// o motivo -- em 400 mostra o `detail` do backend.
function offerErrorMessage(e: unknown): string {
  const err = e as {
    response?: { status?: number; data?: { detail?: unknown } }
  }
  const status = err?.response?.status
  if (status === 401) return 'A sessão expirou. Volta a entrar.'
  if (status === 403) return 'Sem permissão para esta ação.'
  if (!err?.response) return 'Sem ligação ao servidor.'
  const detail = err?.response?.data?.detail
  if (typeof detail === 'string' && detail) return detail
  return 'Não foi possível concluir a ação.'
}

export default function TaskOfferProvider({
  children,
}: {
  children: ReactNode
}) {
  const [offer, setOffer] = useState<TaskDTO | null>(null) // ASSIGNED por aceitar (popup)
  const [activeTask, setActiveTask] = useState<TaskDTO | null>(null) // aceite, em execucao
  const [claimError, setClaimError] = useState<string | null>(null)
  const [offerError, setOfferError] = useState<string | null>(null) // erro da oferta (dentro do popup)
  const navigate = useNavigate()
  const fb = useFeedback()

  // dedupe anti-cache-stale: nao reabrir a MESMA task ja' tratada antes do refetch.
  const lastOfferedIdRef = useRef<number | null>(null)

  // RETOMA APOS QUEDA (ADR-0034 F4b-8): ao MONTAR (apos o gate/login), deteta a atribuicao vigente
  // do operador e reabre a execucao no passo exato. O next_step e' stateless (deriva de Task+
  // TaskAssignment+Reservation) -> a retoma nao precisa de estado guardado, so' de reabrir a tela.
  // getTaskInbox() SEM status resolve o operador do TOKEN e filtra is_current -> traz a task COM
  // lines. Fail-closed no status (so' accepted/in_progress; nunca done/cancelled). 1x ao montar
  // (ref-guard, sem loop). Nao duplica o _maybe_convoke_on_login do backend (que se abstem quando
  // ja' ha is_current) -- aqui e' que se RE-OFERECE a existente ao proprio operador.
  const resumedRef = useRef(false)
  useEffect(() => {
    if (resumedRef.current) return
    resumedRef.current = true
    getTaskInbox({})
      .then(async (tasks) => {
        const resumable = tasks.find(
          (t) => t.status === 'accepted' || t.status === 'in_progress',
        )
        if (!resumable) return
        // G2 (ADR-0034 F4b-7-ii): NAO reabrir uma task cujo next_step ja' e' 'done' -- evita o
        // loop retoma<->conclusao (a Execucao, ao ver 'done', volta a /m; reabrir de novo repetiria).
        try {
          const ns = await getNextStep(resumable.id)
          if (ns.current_step === 'done') return // terminada -> nao reabre
        } catch {
          /* se o next_step falhar, segue a retoma normal (fail-open) */
        }
        setActiveTask(resumable)
        navigate('/m/execucao')
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // HEARTBEAT ALWAYS-ON (ADR-0038 A2): o provider so' monta atras do gate de presenca (ha' sessao
  // ativa) -> bate /sessions/heartbeat/ a cada 30s ENQUANTO montado, incl. durante a execucao (ao
  // contrario do poll do inbox, que para). Sem isto, o lazy-close (A1) mataria todos em 120s. Bate
  // 1x imediato (nao esperar 30s) + a cada 30s. Fail-soft: falha de rede -> ignora (o proximo bate;
  // o A1 so' fecha apos 120s -> ~4 tentativas de folga). Interval independente do poll/oferta.
  useEffect(() => {
    const beat = () => {
      void heartbeatSession().catch(() => {})
    }
    beat() // imediato ao montar
    const iv = setInterval(beat, 30000)
    return () => clearInterval(iv)
  }, [])

  // POLL SEMPRE que ha' sessao (o provider so' monta atras do gate de presenca) e nao ha' oferta
  // aberta nem execucao a decorrer. Pesca a atribuicao vigente que a peca 1 convocou ao logar.
  // O inbox usa o TaskSerializer -> traz `lines` -> a task usa-se direta (= o que o claim faz).
  // INERTE quando a flag esta' OFF (inbox vazio) -> comportamento identico ao de hoje.
  const idle = !offer && !activeTask
  const inboxPoll = useQuery({
    queryKey: ['task-inbox-assigned'],
    queryFn: () => getTaskInbox({ status: 'assigned' }),
    enabled: idle,
    refetchInterval: idle ? 5000 : false,
    refetchOnWindowFocus: false,
  })
  useEffect(() => {
    if (!idle) return
    const next = inboxPoll.data?.[0]
    // guard anti-cache-stale: ao voltar a ocioso apos um finish/reject, a data cacheada pode
    // ainda trazer a task ja' tratada antes do refetch -> nao a reabrir (mesmo id).
    // ADR-0058 (item 3): defesa no cliente -- so' abre se a task ainda for OFERTAVEL (nao morta).
    if (!next || next.id === lastOfferedIdRef.current || !isOfferable(next))
      return
    lastOfferedIdRef.current = next.id
    setOfferError(null)
    setOffer(next) // ASSIGNED-nao-aceite -> mostra a oferta (= o que o claim faz)
  }, [idle, inboxPoll.data])

  // -- pull manual (botao "Proxima tarefa") ---------------------------------
  const claim = useMutation({
    mutationFn: () => claimNextTask({}),
    onSuccess: (t) => {
      setClaimError(null)
      if (!isOfferable(t)) {
        setClaimError('A tarefa já não está disponível.') // defesa (item 3)
        return
      }
      lastOfferedIdRef.current = t.id
      setOfferError(null)
      setOffer(t) // claim atribui (ASSIGNED) -> mostra a oferta primeiro
    },
    onError: (e: any) =>
      setClaimError(
        e?.response?.data?.detail ||
          'Sem tarefas disponiveis ou erro ao reclamar.',
      ),
  })

  // ADR-0058 (itens 1+2): o erro da oferta deixa de ser engolido. Mostra o motivo do backend no
  // popup; e se o 400 for de uma oferta JA' MORTA (a task saiu do inbox 'assigned' -> done/cancelled
  // ou sem atribuicao corrente), limpa a oferta. NAO limpa a's cegas: um 400 transitorio com a task
  // ainda ofertavel mantem a oferta (so' mostra o motivo). Rede/401/403 -> mostra e mantem.
  async function handleOfferActionError(e: unknown) {
    fb.err()
    const status = (e as { response?: { status?: number } })?.response?.status
    const cur = offer
    if (status === 400 && cur) {
      try {
        const inbox = await getTaskInbox({ status: 'assigned' })
        const stillOfferable = inbox.some(
          (t) => t.id === cur.id && isOfferable(t),
        )
        if (!stillOfferable) {
          setOfferError(null)
          setOffer(null) // oferta morta -> fecha o popup (a bottom-sheet desmonta com ele)
          return
        }
      } catch {
        /* rede na re-verificacao -> trata como transitorio (nao perde a oferta) */
      }
    }
    setOfferError(offerErrorMessage(e)) // transitorio / 401 / 403 / rede -> motivo, mantem a oferta
  }

  // -- aceitar a oferta (carimba accepted_at; preserva as lines) -------------
  const accept = useMutation({
    mutationFn: () => acceptTask((offer as TaskDTO).id),
    onSuccess: () => {
      const t = offer as TaskDTO
      setOfferError(null)
      setOffer(null)
      setActiveTask(t) // preserva as lines (o inbox/claim usam o TaskSerializer)
      fb.ok()
      navigate('/m/execucao') // Aceitar -> tela de execucao guiada (ADR-0034 F4b-6-ii)
    },
    onError: (e) => handleOfferActionError(e),
  })

  // -- rejeitar a oferta (com motivo; volta ao buffer + abre pausa no backend) ----
  const reject = useMutation({
    mutationFn: (reasonId: number) =>
      rejectTask((offer as TaskDTO).id, reasonId),
    onSuccess: () => {
      // volta ao buffer + pausa (backend). O poll retoma; enquanto a pausa durar,
      // is_available_for_dispatch=False -> inbox vazio -> sem re-oferta imediata.
      setOfferError(null)
      setOffer(null)
    },
    onError: (e) => handleOfferActionError(e),
  })

  const value: Ctx = {
    activeTask,
    clearActive: () => setActiveTask(null),
    claimNext: () => claim.mutate(),
    claiming: claim.isPending,
    claimError,
  }

  return (
    <TaskOfferContext.Provider value={value}>
      {children}
      {/* POPUP GLOBAL: overlay full-screen (fixed inset-0 z-50) sobre qualquer pagina do /m. */}
      {offer && !activeTask && (
        <TaskOfferPopup
          task={offer}
          onAccept={() => accept.mutate()}
          onReject={(rid) => reject.mutate(rid)}
          busy={accept.isPending || reject.isPending}
          error={offerError}
        />
      )}
    </TaskOfferContext.Provider>
  )
}
